import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ASSISTENT_API_URL } from '@/config/backend'
import { useToast } from '@/hooks/use-toast'
import { useAuthStore } from '@/stores/auth-store'
import { yupResolver } from '@hookform/resolvers/yup'
import axios from 'axios'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'

interface UserProfileForm {
    firstName: string
    lastName: string
    password?: string
    passwordRepeat?: string
}

const schema = yup.object({
    firstName: yup
        .string()
        .trim()
        .required('Vorname ist erforderlich')
        .max(25, 'Maximal 25 Zeichen erlaubt'),
    lastName: yup
        .string()
        .trim()
        .required('Nachname ist erforderlich')
        .max(25, 'Maximal 25 Zeichen erlaubt'),
    password: yup
        .string()
        .transform((value) => (value === '' ? undefined : value))
        .notRequired()
        .min(6, 'Passwort muss mindestens 6 Zeichen lang sein')
        .max(30, 'Passwort darf maximal 30 Zeichen lang sein')
        .matches(
            /^(?=.*[A-Za-z])(?=.*\d).+$/,
            'Passwort muss Buchstaben und Zahlen enthalten'
        ),
    passwordRepeat: yup
        .string()
        .test('passwords-match', 'Passwörter müssen übereinstimmen', function (value) {
            const password = this.parent.password
            if (!password || !password.trim()) return true
            return value === password
        })
        .test('required-if-password', 'Bitte wiederholen Sie das Passwort', function (value) {
            const password = this.parent.password
            if (!password || !password.trim()) return true
            return !!value?.trim()
        }),
})

export function UserProfileForm() {
    const { t } = useTranslation()
    const { user, getApiHeaders, getUserId, userLogout, refreshAuthToken } = useAuthStore()
    const { toast } = useToast()
    const [isLoading, setIsLoading] = useState(false)

    const {
        register,
        handleSubmit,
        formState: { errors, isDirty },
        reset,
    } = useForm<UserProfileForm>({
        resolver: yupResolver(schema) as any,
        defaultValues: {
            firstName: '',
            lastName: '',
            password: '',
            passwordRepeat: '',
        },
    })

    useEffect(() => {
        if (user) {
            reset({
                firstName: user.firstname || '',
                lastName: user.lastname || '',
                password: '',
                passwordRepeat: '',
            })
        }
    }, [user, reset])

    const onSubmit = async (data: UserProfileForm) => {
        if (!isDirty) return

        setIsLoading(true)

        try {
            const url = `${ASSISTENT_API_URL}user/${getUserId()}/update`

            const updateUserDataDto: any = {
                firstName: data.firstName,
                lastName: data.lastName,
            }

            if (data.password) {
                updateUserDataDto.newPassword = data.password
            }

            await axios.put(url, updateUserDataDto, {
                headers: getApiHeaders(),
            })

            toast({
                title: t('success.title'),
                description: data.password
                    ? t('userProfile.saveSuccessLogout')
                    : t('userProfile.saveSuccess'),
            })

            // If password changed, logout user
            if (data.password) {
                setTimeout(() => {
                    userLogout()
                }, 1500)
            } else {
                await refreshAuthToken()
                reset(data)
            }
        } catch (error) {
            console.error('Error updating user profile:', error)
            toast({
                variant: 'destructive',
                title: t('error.title'),
                description: t('userProfile.saveError'),
            })
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Name Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label htmlFor="lastName" className="text-sm font-semibold text-gray-700">
                        {t('global.fieldnames.lastname')}
                    </Label>
                    <Input
                        id="lastName"
                        {...register('lastName')}
                        placeholder={t('global.fieldnames.lastname')}
                        disabled={isLoading}
                        className={errors.lastName ? 'border-red-300' : ''}
                    />
                    {errors.lastName && (
                        <p className="text-sm text-red-600 font-medium">{errors.lastName.message}</p>
                    )}
                </div>

                <div className="space-y-2">
                    <Label htmlFor="firstName" className="text-sm font-semibold text-gray-700">
                        {t('global.fieldnames.firstname')}
                    </Label>
                    <Input
                        id="firstName"
                        {...register('firstName')}
                        placeholder={t('global.fieldnames.firstname')}
                        disabled={isLoading}
                        className={errors.firstName ? 'border-red-300' : ''}
                    />
                    {errors.firstName && (
                        <p className="text-sm text-red-600 font-medium">{errors.firstName.message}</p>
                    )}
                </div>
            </div>

            {/* Password Fields */}
            <div className="space-y-4 pt-4 border-t">
                <div className="space-y-2">
                    <Label htmlFor="password" className="text-sm font-semibold text-gray-700">
                        {t('userProfile.newPassword')}
                    </Label>
                    <Input
                        id="password"
                        type="password"
                        {...register('password')}
                        placeholder={t('userProfile.newPassword')}
                        disabled={isLoading}
                        className={errors.password ? 'border-red-300' : ''}
                    />
                    {errors.password && (
                        <p className="text-sm text-red-600 font-medium">{errors.password.message}</p>
                    )}
                    <p className="text-xs text-gray-500">
                        {t('userProfile.passwordHint')}
                    </p>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="passwordRepeat" className="text-sm font-semibold text-gray-700">
                        {t('global.fieldnames.passwordRepeat')}
                    </Label>
                    <Input
                        id="passwordRepeat"
                        type="password"
                        {...register('passwordRepeat')}
                        placeholder={t('global.fieldnames.passwordRepeat')}
                        disabled={isLoading}
                        className={errors.passwordRepeat ? 'border-red-300' : ''}
                    />
                    {errors.passwordRepeat && (
                        <p className="text-sm text-red-600 font-medium">{errors.passwordRepeat.message}</p>
                    )}
                </div>
            </div>

            {/* Info Note */}
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                    <strong>{t('integrationConfig.notice')}:</strong> {t('userProfile.passwordChangeNote')}
                </p>
            </div>

            {/* Submit Button */}
            <div className="pt-4">
                <Button
                    type="submit"
                    disabled={!isDirty || isLoading}
                    className="inline-flex items-center gap-2"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    {isLoading ? t('global.button.loading') : t('global.button.saveChanges')}
                </Button>
            </div>
        </form>
    )
}
